import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { ReactElement, ReactNode, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
  AccountBinInputPageCvcFlatFields,
  AccountBinInputPageFlatFields,
} from 'src/app/RegisterAccount/RegisterAccount'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useBackgroundDispatch } from 'src/hooks'
import { getLocaleInfo } from 'src/localization/locales'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { BinRangeRegexes } from 'src/models/creditCard.models'
import { getCreditCardsActiveSourceCountStatistics } from 'src/queries/statistics/statistics.actions'
import { selectAllStatisticsLoadedFromState } from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface RegisterAccountBinInputFormProps {
  className?: string
  flatFields: AccountBinInputPageFlatFields
  onFlatFieldChange: (
    fieldName: keyof AccountBinInputPageFlatFields,
    value: AccountBinInputPageFlatFields[keyof AccountBinInputPageFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageFlatFields,
      Array<React.ReactElement | string>
    >
  >
  cvcFlatFields: AccountBinInputPageCvcFlatFields
  onCvcFlatFieldChange: (
    fieldName: keyof AccountBinInputPageCvcFlatFields,
    value: AccountBinInputPageCvcFlatFields[keyof AccountBinInputPageCvcFlatFields],
  ) => void
  cvcFlatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageCvcFlatFields,
      Array<React.ReactElement | string>
    >
  >
  loading?: boolean
  onSubmit: (bin?: string) => void
  stepElement?: ReactElement
  title: string
  subtitle: ReactNode
  toggleSlot?: ReactNode
  cardInfoHeader: React.ReactElement
  boldFieldHeaders?: boolean
  cardNumberPlaceholder?: string
  expirationDatePlaceholder?: string
  cvcPlaceholder?: string
  submitButtonText?: React.ReactNode
  secondaryOption?: React.ReactElement
  topLevelError?: string
}

const RegisterAccountBinInputForm: React.FC<
  RegisterAccountBinInputFormProps
> = ({
  className,
  flatFields,
  onFlatFieldChange,
  flatFieldsErrors,
  cvcFlatFields,
  onCvcFlatFieldChange,
  cvcFlatFieldsErrors,
  onSubmit,
  stepElement,
  title,
  subtitle,
  toggleSlot,
  cardInfoHeader,
  topLevelError,
  loading,
  boldFieldHeaders,
  cardNumberPlaceholder,
  expirationDatePlaceholder,
  cvcPlaceholder,
  submitButtonText,
  secondaryOption,
}): React.ReactNode => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)

  const dispatch = useBackgroundDispatch()

  useEffect((): void => {
    dispatch(getCreditCardsActiveSourceCountStatistics())
  }, [])

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    onSubmit(flatFields?.cardNumber)
  }

  // TODO: make a general utility function out of this
  const formattingFunction = (val: string): ((val: string) => string) => {
    const noSpacesOnlyNums = val.replace(/\s+/g, '').replace(/[^\d]/g, '')

    // amex (15 digit range)
    if (noSpacesOnlyNums.match(BinRangeRegexes.amex)) {
      return (): string => {
        return noSpacesOnlyNums.length > 10
          ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 10)} ${noSpacesOnlyNums.slice(10, 15)}`
          : noSpacesOnlyNums.length > 4
            ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 10)}`
            : noSpacesOnlyNums
      }
    } else if (noSpacesOnlyNums.match(BinRangeRegexes.diners)) {
      return (): string => {
        return noSpacesOnlyNums.length > 10
          ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 10)} ${noSpacesOnlyNums.slice(10, 14)}`
          : noSpacesOnlyNums.length > 4
            ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 10)}`
            : noSpacesOnlyNums
      }
    } else {
      return (): string => {
        return noSpacesOnlyNums.length > 12
          ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 8)} ${noSpacesOnlyNums.slice(8, 12)} ${noSpacesOnlyNums.slice(12, 16)}`
          : noSpacesOnlyNums.length > 8
            ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 8)} ${noSpacesOnlyNums.slice(8, 12)}`
            : noSpacesOnlyNums.length > 4
              ? `${noSpacesOnlyNums.slice(0, 4)} ${noSpacesOnlyNums.slice(4, 8)}`
              : noSpacesOnlyNums
      }
    }
  }

  const dateFormattingFunction = (val: string): ((val: string) => string) => {
    if (val.length <= getLocaleInfo(locale).shortDateFormat.length) {
      if (val.length > 2 && !val.includes('/')) {
        return (val: string): string => {
          return `${val.slice(0, 2)}/${val.slice(2, 4)}`
        }
      } else {
        return (val: string): string => {
          return val
        }
      }
    } else {
      return (val: string): string => {
        return `${val.slice(0, 5)}`
      }
    }
  }

  const restrictToNumbers = (val: string): string => {
    return val.replace(/\s+/g, '').replace(/[^\d]/g, '')
  }

  // References for the Form Inputs used to focus corresponding inputs.
  const formFieldsRefObj = {
    cardNumber: useRef<HTMLInputElement>(null),
    expirationDate: useRef<HTMLInputElement>(null),
    cvc: useRef<HTMLInputElement>(null),
  }

  return !statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded ? (
    <LoadingBackgroundPage />
  ) : (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 60px 60px 40px 60px;
      `}
      className={className}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          max-width: 440px;
        `}
      >
        {stepElement}
        <span>{title}</span>
      </div>
      <div
        css={css`
          margin-top: 30px;
          max-width: 500px;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            max-width: none;
          `)}
          margin-bottom: 20px;
        `}
      >
        {subtitle}
      </div>
      {toggleSlot}
      <form
        css={css`
          display: flex;
          flex-direction: column;
        `}
        onSubmit={handleOnSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          {cardInfoHeader}
          <div ref={formFieldsRefObj.cardNumber}>
            <TextInputField
              id={'cardNumber'}
              errors={flatFieldsErrors['cardNumber']}
              design={TextInputFieldDesigns.New}
              fieldLabel={
                <span
                  css={css`
                    ${boldFieldHeaders &&
                    css`
                      font-weight: bold;
                    `}
                  `}
                >
                  {translate('Card Number')}
                </span>
              }
              value={flatFields.cardNumber?.toString()}
              onChange={(val: string): void => {
                onFlatFieldChange('cardNumber', formattingFunction(val)(val))
              }}
              placeholder={
                cardNumberPlaceholder ||
                translateSingleString(
                  locale,
                  'Enter your 15 or 16-digit card number',
                )
              }
            />
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              width: 100%;
              gap: 40px;
            `}
          >
            <div
              css={css`
                margin-top: 20px;
                width: 50%;
              `}
              ref={formFieldsRefObj.expirationDate}
            >
              <TextInputField
                id={'expirationDate'}
                errors={flatFieldsErrors['expirationDate']}
                design={TextInputFieldDesigns.New}
                fieldLabel={
                  <span
                    css={css`
                      ${boldFieldHeaders &&
                      css`
                        font-weight: bold;
                      `}
                    `}
                  >
                    {translate('Expiration Date')}
                  </span>
                }
                value={flatFields.expirationDate}
                onChange={(val: string): void => {
                  onFlatFieldChange(
                    'expirationDate',
                    dateFormattingFunction(val)(val),
                  )
                }}
                placeholder={
                  expirationDatePlaceholder ||
                  translateSingleString(
                    locale,
                    getLocaleInfo(locale).shortDateFormat,
                  )
                }
              />
            </div>
            <div
              css={css`
                margin-top: 20px;
                width: 50%;
              `}
              ref={formFieldsRefObj.cvc}
            >
              <TextInputField
                id={'cvc'}
                errors={cvcFlatFieldsErrors['cvc']}
                design={TextInputFieldDesigns.New}
                fieldLabel={
                  <span
                    css={css`
                      ${boldFieldHeaders &&
                      css`
                        font-weight: bold;
                      `}
                    `}
                  >
                    {translate('CVC (if applicable)')}
                  </span>
                }
                value={cvcFlatFields.cvc?.toString()}
                onChange={(val: string): void => {
                  onCvcFlatFieldChange('cvc', restrictToNumbers(val))
                }}
                placeholder={
                  cvcPlaceholder ||
                  translateSingleString(locale, 'Enter CVC if applicable')
                }
              />
            </div>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              gap: 24px;
              margin-top: 40px;
              ${secondaryOption !== undefined &&
              css`
                justify-content: space-between;
              `}
            `}
          >
            {secondaryOption}
            <Button
              id={'accountNameButton'}
              data-testid="next-button"
              type={ButtonTypes.submit}
              design={ButtonDesigns.designCta}
              text={submitButtonText || translate('Continue to Billing Info')}
              big
              loading={loading}
            />
          </div>
          {topLevelError !== undefined && (
            <span
              css={css`
                margin-top: 40px;
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  margin-top: 30px;
                `)}
              `}
            >
              {topLevelError}
            </span>
          )}
        </div>
      </form>
    </div>
  )
}

export default RegisterAccountBinInputForm
